import React, { useState, useEffect, useCallback, useRef } from 'react';
import './UserManagement.css';
import { formatDistanceToNow } from 'date-fns';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import api, {
    addUser,
    setUserStatus,
    updateUserDetails,
    assignTherapistForClient,
    updateUserRoles,
    getPaginatedUsers,
    bulkUpdateUserStatus,
    exportUsers,
    resetUserPassword
} from '../../services/api';
import TextToSpeech from '../TextToSpeech';
import axios from 'axios';
// MUI Imports for Modal and Form Controls
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import DialogContentText from '@mui/material/DialogContentText';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function UserManagement({ token }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // Sign-in security actions in the edit dialog (security batches 3 and 4)
    const [securityActionMsg, setSecurityActionMsg] = useState('');
    const runSecurityAction = async (path, doneText) => {
        setSecurityActionMsg('');
        try {
            await api.post(path, {});
            setSecurityActionMsg(doneText);
        } catch (err) {
            setSecurityActionMsg(err?.response?.data?.msg || err.message || 'That did not work. Please try again.');
        }
    };
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [availableModalities, setAvailableModalities] = useState([]);
    const [selectedSpecializations, setSelectedSpecializations] = useState([]);
    const [elevenLabsVoiceIdField, setElevenLabsVoiceIdField] = useState('');
    const [availableElevenLabsVoices, setAvailableElevenLabsVoices] = useState([]); // State for ElevenLabs voices list
    const [sampleTextModal, setSampleTextModal] = useState('Previewing therapist voice.'); // State for sample text in modal
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState('');
    const [statusChangeDialog, setStatusChangeDialog] = useState({ open: false, user: null, newStatus: null });
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

    // Pagination state
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
    const [sortBy] = useState('name');
    const [sortOrder] = useState('asc');

    // Filter state
    const [filters, setFilters] = useState({ role: '', status: '', serviceGroupId: '', noServiceGroup: false });
    const [serviceGroups, setServiceGroups] = useState([]);
    const [allTherapists, setAllTherapists] = useState([]);
    const [serviceGroupToAdd, setServiceGroupToAdd] = useState('');
    // Empty string = auto-detect from User.role (legacy behaviour: maps
    // training_admin/clinical_supervisor/therapist/lecturer/student to the
    // corresponding SG-membership role; otherwise client).
    const [serviceGroupRoleOverride, setServiceGroupRoleOverride] = useState('');

    // Bulk selection state
    const [selectedUsers, setSelectedUsers] = useState(new Set());
    const [selectAll, setSelectAll] = useState(false);

    // Debounce timer ref
    const searchTimeoutRef = useRef(null);

    // Edit User Modal state
    const [editUserModalOpen, setEditUserModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    // "Add User" dialog
    const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
    const [newUserForm, setNewUserForm] = useState({
        name: '',
        email: '',
        password: '',
        role: 'client',
        forcePasswordChange: true,
    });
    const [addUserSubmitting, setAddUserSubmitting] = useState(false);
    const [editForm, setEditForm] = useState({
        name: '',
        email: '',
        phone: '',
        status: 'Active',
        roles: [],
        forcePasswordChange: false,
        assignedTherapist: ''
    });
    const [editFormLoading, setEditFormLoading] = useState(false);
    const [activationEmailDialog, setActivationEmailDialog] = useState({ open: false, callback: null });

    // Password Reset Dialog state
    const [passwordResetDialog, setPasswordResetDialog] = useState({
        open: false,
        user: null,
        newPassword: '',
        forceChange: true
    });

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                sortBy,
                sortOrder,
                ...(filters.role && { role: filters.role }),
                ...(filters.status && { status: filters.status }),
                ...(filters.serviceGroupId && { serviceGroupId: filters.serviceGroupId }),
                ...(filters.noServiceGroup && { noServiceGroup: 'true' }),
                ...(debouncedSearchQuery.trim() && { search: debouncedSearchQuery.trim() })
            };
            const response = await getPaginatedUsers(params, token);
            setUsers(response.users || []);
            setPagination(prev => ({
                ...prev,
                total: response.pagination.total,
                pages: response.pagination.pages
            }));
            setSelectedUsers(new Set()); // Clear selection on data refresh
            setSelectAll(false);
        } catch (err) {
            setError('Failed to fetch users.');
        } finally {
            setLoading(false);
        }
    }, [token, pagination.page, pagination.limit, sortBy, sortOrder, filters, debouncedSearchQuery]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Fetch service groups for filter dropdown
    useEffect(() => {
        const fetchServiceGroups = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/service-groups`, {
                    headers: { 'x-auth-token': token }
                });
                setServiceGroups(res.data.data || []);
            } catch (err) {
                console.error('Error fetching service groups:', err);
            }
        };
        if (token) {
            fetchServiceGroups();
        }
    }, [token]);

    // Fetch all therapists for the assignment dropdown
    useEffect(() => {
        const fetchTherapists = async () => {
            try {
                const params = {
                    role: 'therapist',
                    status: 'Active',
                    limit: 1000
                };
                const response = await getPaginatedUsers(params, token);
                setAllTherapists(response.users || []);
            } catch (err) {
                console.error('Failed to fetch therapists:', err);
            }
        };
        if (token) {
            fetchTherapists();
        }
    }, [token]);

    // Debounced search - reset to page 1 when search changes
    const handleSearchChange = useCallback((value) => {
        setSearchQuery(value);
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchQuery(value);
            setPagination(prev => ({ ...prev, page: 1 }));
        }, 500);
    }, []);

    // Bulk selection handlers
    const handleSelectUser = (userId) => {
        setSelectedUsers(prev => {
            const next = new Set(prev);
            if (next.has(userId)) {
                next.delete(userId);
            } else {
                next.add(userId);
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedUsers(new Set());
        } else {
            setSelectedUsers(new Set(users.filter(u => !u.role.includes('admin')).map(u => u._id)));
        }
        setSelectAll(!selectAll);
    };

    const handleBulkStatusChange = async (newStatus) => {
        if (selectedUsers.size === 0) return;
        try {
            await bulkUpdateUserStatus([...selectedUsers], newStatus, token);
            fetchUsers();
        } catch (err) {
            setError(err.message || 'Bulk update failed');
        }
    };

    const handleExport = async () => {
        try {
            const params = {
                ...(filters.role && { role: filters.role }),
                ...(filters.status && { status: filters.status }),
                ...(filters.serviceGroupId && { serviceGroupId: filters.serviceGroupId }),
                ...(debouncedSearchQuery.trim() && { search: debouncedSearchQuery.trim() })
            };
            const csvData = await exportUsers(params, token);
            const blob = new Blob([csvData], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(err.message || 'Export failed');
        }
    };

    // Edit User Modal handlers
    const handleOpenEditModal = (user) => {
        setEditingUser(user);
        setEditForm({
            name: user.name || '',
            email: user.email || '',
            phone: user.phone || '',
            status: user.status || 'Active',
            roles: [...(user.role || [])],
            forcePasswordChange: user.forcePasswordChange || false,
            assignedTherapist: user.assignedTherapist?._id || user.assignedTherapist || ''
        });
        setEditUserModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setEditUserModalOpen(false);
        setEditingUser(null);
        setEditForm({
            name: '',
            email: '',
            phone: '',
            status: 'Active',
            roles: [],
            forcePasswordChange: false,
            assignedTherapist: ''
        });
        setServiceGroupToAdd('');
    };

    const handleSaveUser = async (sendActivationEmail = false) => {
        if (!editingUser) return;

        setEditFormLoading(true);
        try {
            const updates = {};
            const originalUser = editingUser;

            // Check what changed and build updates
            if (editForm.name !== originalUser.name) {
                updates.name = editForm.name;
            }
            if (editForm.email !== originalUser.email) {
                updates.email = editForm.email;
            }
            if (editForm.phone !== (originalUser.phone || '')) {
                updates.phone = editForm.phone;
            }
            if (editForm.forcePasswordChange !== (originalUser.forcePasswordChange || false)) {
                updates.forcePasswordChange = editForm.forcePasswordChange;
            }

            // Update basic details if any changed
            if (Object.keys(updates).length > 0) {
                await updateUserDetails(editingUser._id, updates, token);
            }

            // Update status if changed
            const originalStatus = originalUser.status || 'Active';
            if (editForm.status !== originalStatus) {
                await setUserStatus(editingUser._id, editForm.status, token, sendActivationEmail);
            }

            // Update roles if changed
            const originalRoles = originalUser.role || [];
            const rolesChanged = editForm.roles.length !== originalRoles.length ||
                !editForm.roles.every(r => originalRoles.includes(r));
            if (rolesChanged) {
                await updateUserRoles(editingUser._id, editForm.roles, token);
            }

            // Update therapist assignment if changed (for clients)
            const originalTherapist = originalUser.assignedTherapist?._id || originalUser.assignedTherapist || '';
            if (editForm.roles.includes('client') && editForm.assignedTherapist !== originalTherapist) {
                await assignTherapistForClient(editingUser._id, editForm.assignedTherapist, token);
            }

            handleCloseEditModal();
            fetchUsers();
        } catch (err) {
            setError(err.message || 'Failed to save user changes');
        } finally {
            setEditFormLoading(false);
        }
    };

    const handleSaveClick = () => {
        // Check if activating from Pending - ask about activation email
        const originalStatus = editingUser?.status || 'Active';
        if (originalStatus === 'Pending' && editForm.status === 'Active') {
            setActivationEmailDialog({
                open: true,
                callback: (sendEmail) => {
                    setActivationEmailDialog({ open: false, callback: null });
                    handleSaveUser(sendEmail);
                }
            });
        } else {
            handleSaveUser(false);
        }
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [modalities, voices] = await Promise.all([
                    api.getAvailableModalities(token),
                    api.getElevenLabsVoices() // Server-backed (/api/tts/voices); auth via apiClient defaults
                ]);
                setAvailableModalities(modalities || []);
                setAvailableElevenLabsVoices(voices || []);
            } catch (err) {
                setError('Failed to load initial admin data (specializations or voices).');
                console.error("Error fetching initial admin data:", err);
            }
        };
        if (token) {
            fetchInitialData();
        }
    }, [token]);

    const handleOpenModal = (user) => {
        setSelectedUser(user);
        setSelectedSpecializations(user.therapistInfo?.specializations || []);
        setElevenLabsVoiceIdField(user.therapistInfo?.elevenLabsVoiceId || ''); // Initialize voice ID field
        setModalError('');
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedUser(null);
        setSelectedSpecializations([]);
        setElevenLabsVoiceIdField(''); // Reset voice ID field
        setModalError('');
        setModalLoading(false);
    };

    const handleSpecializationChange = (event) => {
        const { target: { value } } = event;
        setSelectedSpecializations(typeof value === 'string' ? value.split(',') : value);
    };

    const handleSaveChanges = async () => {
        if (!selectedUser) return;
        setModalLoading(true);
        setModalError('');
        try {
            // Update specializations and voice ID
            // Using Promise.all to run them concurrently, or they can be sequential
            await Promise.all([
                api.updateTherapistSpecializations(selectedUser._id, selectedSpecializations, token),
                api.updateTherapistElevenLabsVoiceId(selectedUser._id, elevenLabsVoiceIdField, token)
            ]);
            fetchUsers(); // Refresh the user list
            handleCloseModal();
        } catch (err) {
            setModalError(err.message || 'Failed to save therapist settings.');
        } finally {
            setModalLoading(false);
        }
    };

    const handleStatusChange = async (userId, newStatus, sendActivationEmail) => {
        try {
            const result = await setUserStatus(userId, newStatus, sendActivationEmail, token);
            if (result.emailSent) {
                setError(null);
                alert('Status updated and activation email sent successfully!');
            }
            fetchUsers();
        } catch (err) {
            setError(err.message || 'Failed to update user status');
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
    }

    if (error && users.length === 0) {
        return <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>;
    }

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>User Management</Typography>
            {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Search and Filter Bar */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    size="small"
                    label="Search users"
                    placeholder="Name, email, or ID..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    sx={{ minWidth: 250 }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Role</InputLabel>
                    <Select
                        value={filters.role}
                        label="Role"
                        onChange={(e) => {
                            setFilters(f => ({ ...f, role: e.target.value }));
                            setPagination(p => ({ ...p, page: 1 }));
                        }}
                    >
                        <MenuItem value="">All Roles</MenuItem>
                        <MenuItem value="client">Client</MenuItem>
                        <MenuItem value="therapist">Therapist</MenuItem>
                        <MenuItem value="clinical_supervisor">Clinical Supervisor</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                        <MenuItem value="student">Student</MenuItem>
                        <MenuItem value="lecturer">Lecturer</MenuItem>
                        <MenuItem value="training_admin">Training Admin</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={filters.status}
                        label="Status"
                        onChange={(e) => {
                            setFilters(f => ({ ...f, status: e.target.value }));
                            setPagination(p => ({ ...p, page: 1 }));
                        }}
                    >
                        <MenuItem value="">All Statuses</MenuItem>
                        <MenuItem value="Active">Active</MenuItem>
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Suspended">Suspended</MenuItem>
                        <MenuItem value="Marked for Deletion">Marked for Deletion</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Service Group</InputLabel>
                    <Select
                        value={filters.noServiceGroup ? '__none__' : filters.serviceGroupId}
                        label="Service Group"
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === '__none__') {
                                setFilters(f => ({ ...f, serviceGroupId: '', noServiceGroup: true }));
                            } else {
                                setFilters(f => ({ ...f, serviceGroupId: value, noServiceGroup: false }));
                            }
                            setPagination(p => ({ ...p, page: 1 }));
                        }}
                    >
                        <MenuItem value="">All Service Groups</MenuItem>
                        <MenuItem value="__none__" sx={{ fontStyle: 'italic', color: 'warning.main' }}>No Service Group</MenuItem>
                        {serviceGroups.map(sg => (
                            <MenuItem key={sg._id} value={sg._id}>{sg.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                        setFilters({ role: '', status: '', serviceGroupId: '', noServiceGroup: false });
                        setSearchQuery('');
                        setDebouncedSearchQuery('');
                        setPagination(p => ({ ...p, page: 1 }));
                    }}
                >
                    Clear
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleExport}
                >
                    Export CSV
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                    {pagination.total} users
                </Typography>
            </Box>

            {/* Quick Filter Chips */}
            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                    label="Pending Users"
                    onClick={() => { setFilters(f => ({ ...f, status: 'Pending' })); setPagination(p => ({ ...p, page: 1 })); }}
                    variant={filters.status === 'Pending' ? 'filled' : 'outlined'}
                    size="small"
                    color={filters.status === 'Pending' ? 'primary' : 'default'}
                />
                <Chip
                    label="Suspended"
                    onClick={() => { setFilters(f => ({ ...f, status: 'Suspended' })); setPagination(p => ({ ...p, page: 1 })); }}
                    variant={filters.status === 'Suspended' ? 'filled' : 'outlined'}
                    size="small"
                    color={filters.status === 'Suspended' ? 'warning' : 'default'}
                />
                <Chip
                    label="Clients"
                    onClick={() => { setFilters(f => ({ ...f, role: 'client' })); setPagination(p => ({ ...p, page: 1 })); }}
                    variant={filters.role === 'client' ? 'filled' : 'outlined'}
                    size="small"
                    color={filters.role === 'client' ? 'primary' : 'default'}
                />
                <Chip
                    label="Therapists"
                    onClick={() => { setFilters(f => ({ ...f, role: 'therapist' })); setPagination(p => ({ ...p, page: 1 })); }}
                    variant={filters.role === 'therapist' ? 'filled' : 'outlined'}
                    size="small"
                    color={filters.role === 'therapist' ? 'primary' : 'default'}
                />
                <Button
                    className="user-management-3d-button"
                    size="small"
                    onClick={() => {
                        setNewUserForm({
                            name: '',
                            email: '',
                            password: '',
                            role: 'client',
                            forcePasswordChange: true,
                        });
                        setAddUserDialogOpen(true);
                    }}
                    sx={{ ml: 'auto' }}
                >
                    Add User
                </Button>
            </Box>

            {/* Bulk Actions Bar */}
            {selectedUsers.size > 0 && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.selected', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2">{selectedUsers.size} selected</Typography>
                    <Button size="small" color="success" variant="outlined" onClick={() => handleBulkStatusChange('Active')}>Activate</Button>
                    <Button size="small" color="warning" variant="outlined" onClick={() => handleBulkStatusChange('Suspended')}>Suspend</Button>
                    <Button size="small" color="error" variant="outlined" onClick={() => handleBulkStatusChange('Marked for Deletion')}>Mark for Deletion</Button>
                    <Button size="small" onClick={() => { setSelectedUsers(new Set()); setSelectAll(false); }}>Clear Selection</Button>
                </Box>
            )}

            {users.length === 0 ? (
                <Typography>
                    {debouncedSearchQuery || filters.role || filters.status || filters.serviceGroupId
                        ? 'No users found matching your filters.'
                        : 'No users found.'}
                </Typography>
            ) : (
                <>
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox checked={selectAll} onChange={handleSelectAll} />
                                </TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Roles</TableCell>
                                <TableCell>Service Groups</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Last Active</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user, idx) => (
                                <TableRow key={user._id} sx={{ backgroundColor: idx % 2 === 0 ? '#fafafa' : '#fff' }}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedUsers.has(user._id)}
                                            onChange={() => handleSelectUser(user._id)}
                                            disabled={user.role.includes('admin')}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {user.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{user.email}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={user.status || 'Active'}
                                            size="small"
                                            color={
                                                user.status === 'Active' ? 'success' :
                                                user.status === 'Pending' ? 'warning' :
                                                user.status === 'Suspended' ? 'error' : 'default'
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {user.role.map(r => (
                                                <Chip
                                                    key={r}
                                                    label={r === 'clinical_supervisor' ? 'Supervisor' : r === 'student' ? 'Student' : r === 'lecturer' ? 'Lecturer' : r.charAt(0).toUpperCase() + r.slice(1)}
                                                    size="small"
                                                    variant="outlined"
                                                    color={r === 'admin' ? 'secondary' : 'default'}
                                                    sx={{ fontSize: '0.7rem' }}
                                                />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {user.serviceGroups?.filter(sg => sg.isActive).length > 0
                                                ? user.serviceGroups.filter(sg => sg.isActive).map(sg => (
                                                    <Chip
                                                        key={sg.groupId?._id || sg.groupId}
                                                        label={sg.groupId?.name || 'Unknown'}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.7rem' }}
                                                    />
                                                ))
                                                : <Typography variant="body2" color="text.secondary">-</Typography>
                                            }
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                            {user.date ? new Date(user.date).toLocaleDateString() : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {user.lastActivity ? (
                                            <Tooltip title={new Date(user.lastActivity).toLocaleString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric',
                                                hour: 'numeric',
                                                minute: '2-digit'
                                            })}>
                                                <Typography variant="body2" sx={{ fontSize: '0.75rem', cursor: 'default' }}>
                                                    {formatDistanceToNow(new Date(user.lastActivity), { addSuffix: true })}
                                                </Typography>
                                            </Tooltip>
                                        ) : (
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>-</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Tooltip title="Edit User">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenEditModal(user)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {user.role.includes('therapist') && (
                                                <Tooltip title="Voice Settings">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenModal(user)}
                                                    >
                                                        <RecordVoiceOverIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination Controls */}
                {pagination.total > 0 && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Box>
                            <Button
                                size="small"
                                disabled={pagination.page === 1}
                                onClick={() => setPagination(p => ({ ...p, page: 1 }))}
                                sx={{ mr: 0.5 }}
                            >
                                First
                            </Button>
                            <Button
                                size="small"
                                disabled={pagination.page === 1}
                                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                            >
                                Prev
                            </Button>
                        </Box>
                        <Typography variant="body2">
                            Page {pagination.page} of {pagination.pages || 1}
                        </Typography>
                        <Box>
                            <Button
                                size="small"
                                disabled={pagination.page >= pagination.pages}
                                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                            >
                                Next
                            </Button>
                            <Button
                                size="small"
                                disabled={pagination.page >= pagination.pages}
                                onClick={() => setPagination(p => ({ ...p, page: pagination.pages }))}
                                sx={{ ml: 0.5 }}
                            >
                                Last
                            </Button>
                        </Box>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                                value={pagination.limit}
                                onChange={(e) => setPagination(p => ({ ...p, limit: parseInt(e.target.value), page: 1 }))}
                            >
                                <MenuItem value={25}>25/page</MenuItem>
                                <MenuItem value={50}>50/page</MenuItem>
                                <MenuItem value={100}>100/page</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                )}
                </>
            )}

            {/* Add User Dialog */}
            <Dialog
                open={addUserDialogOpen}
                onClose={() => !addUserSubmitting && setAddUserDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Add New User</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Name"
                            fullWidth
                            required
                            value={newUserForm.name}
                            onChange={(e) => setNewUserForm(f => ({ ...f, name: e.target.value }))}
                            autoFocus
                        />
                        <TextField
                            label="Email"
                            type="email"
                            fullWidth
                            required
                            value={newUserForm.email}
                            onChange={(e) => setNewUserForm(f => ({ ...f, email: e.target.value }))}
                        />
                        <TextField
                            label="Password"
                            type="password"
                            fullWidth
                            required
                            value={newUserForm.password}
                            onChange={(e) => setNewUserForm(f => ({ ...f, password: e.target.value }))}
                            helperText="The user can change this on first login if forced below"
                        />
                        <FormControl fullWidth required>
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={newUserForm.role}
                                label="Role"
                                onChange={(e) => setNewUserForm(f => ({ ...f, role: e.target.value }))}
                            >
                                <MenuItem value="client">Client</MenuItem>
                                <MenuItem value="therapist">Therapist</MenuItem>
                                <MenuItem value="clinical_supervisor">Clinical Supervisor</MenuItem>
                                <MenuItem value="admin">Admin</MenuItem>
                                <MenuItem value="lecturer">Lecturer</MenuItem>
                                <MenuItem value="student">Student</MenuItem>
                                <MenuItem value="researcher">Researcher</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={newUserForm.forcePasswordChange}
                                    onChange={(e) => setNewUserForm(f => ({ ...f, forcePasswordChange: e.target.checked }))}
                                />
                            }
                            label="Require user to change password on first login"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddUserDialogOpen(false)} disabled={addUserSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={addUserSubmitting || !newUserForm.name || !newUserForm.email || !newUserForm.password || !newUserForm.role}
                        onClick={async () => {
                            try {
                                setAddUserSubmitting(true);
                                setError('');
                                await addUser({
                                    name: newUserForm.name,
                                    email: newUserForm.email,
                                    password: newUserForm.password,
                                    role: [newUserForm.role],
                                    forcePasswordChange: newUserForm.forcePasswordChange,
                                }, token);
                                setAddUserDialogOpen(false);
                                await fetchUsers();
                            } catch (err) {
                                setError(err.message || 'Failed to add user');
                            } finally {
                                setAddUserSubmitting(false);
                            }
                        }}
                    >
                        {addUserSubmitting ? 'Creating…' : 'Create User'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit User Modal */}
            <Dialog open={editUserModalOpen} onClose={handleCloseEditModal} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Edit User: {editingUser?.name}
                    {editingUser?.role?.includes('admin') && (
                        <Chip label="Admin" size="small" color="secondary" sx={{ ml: 1 }} />
                    )}
                </DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace', display: 'block', mb: 2 }}>
                        User ID: {editingUser?._id}
                    </Typography>

                    <TextField
                        fullWidth
                        margin="normal"
                        label="Name"
                        value={editForm.name}
                        onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
                        disabled={editingUser?.role?.includes('admin')}
                    />

                    <TextField
                        fullWidth
                        margin="normal"
                        label="Email"
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))}
                        disabled={editingUser?.role?.includes('admin')}
                    />

                    <TextField
                        fullWidth
                        margin="normal"
                        label="Phone"
                        value={editForm.phone}
                        onChange={(e) => setEditForm(f => ({ ...f, phone: e.target.value }))}
                        disabled={editingUser?.role?.includes('admin')}
                    />

                    <Divider sx={{ my: 2 }} />

                    <FormControl fullWidth margin="normal">
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={editForm.status}
                            label="Status"
                            onChange={(e) => setEditForm(f => ({ ...f, status: e.target.value }))}
                            disabled={editingUser?.role?.includes('admin')}
                        >
                            {['Active', 'Suspended', 'Pending', 'Marked for Deletion', 'Suspect'].map(status => (
                                <MenuItem key={status} value={status}>{status}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={editForm.forcePasswordChange}
                                onChange={(e) => setEditForm(f => ({ ...f, forcePasswordChange: e.target.checked }))}
                                disabled={editingUser?.role?.includes('admin')}
                            />
                        }
                        label="Force Password Change on Next Login"
                        sx={{ mt: 1 }}
                    />

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>User Roles</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {['client', 'therapist', 'clinical_supervisor', 'student', 'lecturer', 'training_admin'].map((role) => (
                            <FormControlLabel
                                key={role}
                                control={
                                    <Checkbox
                                        checked={editForm.roles.includes(role)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setEditForm(f => ({ ...f, roles: [...f.roles, role] }));
                                            } else {
                                                setEditForm(f => ({ ...f, roles: f.roles.filter(r => r !== role) }));
                                            }
                                        }}
                                        disabled={editingUser?.role?.includes('admin')}
                                    />
                                }
                                label={role === 'clinical_supervisor' ? 'Clinical Supervisor' : role === 'training_admin' ? 'Training Admin' : role.charAt(0).toUpperCase() + role.slice(1)}
                            />
                        ))}
                        {editingUser?.role?.includes('admin') && (
                            <Chip label="Admin" size="small" color="secondary" />
                        )}
                    </Box>

                    {/* Therapist Assignment (only for clients) */}
                    {editForm.roles.includes('client') && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Assigned Therapist</InputLabel>
                                <Select
                                    value={editForm.assignedTherapist}
                                    label="Assigned Therapist"
                                    onChange={(e) => setEditForm(f => ({ ...f, assignedTherapist: e.target.value }))}
                                    disabled={editingUser?.role?.includes('admin')}
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {allTherapists.map(therapist => (
                                        <MenuItem key={therapist._id} value={therapist._id}>
                                            {therapist.name} ({therapist.email})
                                            {therapist.role.includes('clinical_supervisor') && ' [Supervisor]'}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </>
                    )}

                    {/* Service Groups (editable) */}
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Service Groups</Typography>

                    {/* Current memberships with remove buttons */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                        {editingUser?.serviceGroups?.filter(sg => sg.isActive).length > 0 ? (
                            editingUser.serviceGroups.filter(sg => sg.isActive).map(sg => (
                                <Chip
                                    key={sg.groupId?._id || sg.groupId}
                                    label={`${sg.groupId?.name || 'Unknown'}${(sg.roles && sg.roles.length) ? ` — ${sg.roles.join(', ')}` : ''}`}
                                    size="small"
                                    onDelete={async () => {
                                        if (window.confirm(`Remove ${editingUser.name} from ${sg.groupId?.name}?`)) {
                                            try {
                                                await axios.delete(
                                                    `${API_BASE_URL}/service-groups/${sg.groupId?._id || sg.groupId}/members/${editingUser._id}`,
                                                    { headers: { 'x-auth-token': token } }
                                                );
                                                // Update local state to reflect removal
                                                setEditingUser(prev => ({
                                                    ...prev,
                                                    serviceGroups: prev.serviceGroups.map(s =>
                                                        (s.groupId?._id || s.groupId) === (sg.groupId?._id || sg.groupId)
                                                            ? { ...s, isActive: false }
                                                            : s
                                                    )
                                                }));
                                                fetchUsers();
                                            } catch (err) {
                                                setError(err.response?.data?.msg || 'Failed to remove from service group');
                                            }
                                        }
                                    }}
                                    disabled={editingUser?.role?.includes('admin')}
                                />
                            ))
                        ) : (
                            <Typography variant="body2" color="text.secondary">No service groups assigned</Typography>
                        )}
                    </Box>

                    {/* Add to service group */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Add to Service Group</InputLabel>
                            <Select
                                value={serviceGroupToAdd}
                                label="Add to Service Group"
                                onChange={(e) => setServiceGroupToAdd(e.target.value)}
                                disabled={editingUser?.role?.includes('admin')}
                            >
                                <MenuItem value="">Select...</MenuItem>
                                {serviceGroups
                                    .filter(sg => !editingUser?.serviceGroups?.some(
                                        usg => usg.isActive && (usg.groupId?._id || usg.groupId) === sg._id
                                    ))
                                    .map(sg => (
                                        <MenuItem key={sg._id} value={sg._id}>{sg.name}</MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Role in Group</InputLabel>
                            <Select
                                value={serviceGroupRoleOverride}
                                label="Role in Group"
                                onChange={(e) => setServiceGroupRoleOverride(e.target.value)}
                                disabled={editingUser?.role?.includes('admin')}
                            >
                                <MenuItem value="">Auto-detect from user role</MenuItem>
                                <MenuItem value="client">Client</MenuItem>
                                <MenuItem value="therapist">Therapist</MenuItem>
                                <MenuItem value="supervisor">Clinical Supervisor</MenuItem>
                                <MenuItem value="coordinator">Coordinator</MenuItem>
                                <MenuItem value="training_admin">Training Admin</MenuItem>
                                <MenuItem value="lecturer">Lecturer</MenuItem>
                                <MenuItem value="student">Student</MenuItem>
                            </Select>
                        </FormControl>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={!serviceGroupToAdd || editingUser?.role?.includes('admin')}
                            onClick={async () => {
                                if (!serviceGroupToAdd) return;
                                try {
                                    // Use explicit override if set; otherwise auto-detect from user's global roles.
                                    let memberRole = serviceGroupRoleOverride;
                                    if (!memberRole) {
                                        memberRole = 'client';
                                        if (editingUser.role.includes('training_admin')) memberRole = 'training_admin';
                                        else if (editingUser.role.includes('clinical_supervisor')) memberRole = 'supervisor';
                                        else if (editingUser.role.includes('therapist')) memberRole = 'therapist';
                                        else if (editingUser.role.includes('lecturer')) memberRole = 'lecturer';
                                        else if (editingUser.role.includes('student')) memberRole = 'student';
                                    }

                                    await axios.post(
                                        `${API_BASE_URL}/service-groups/${serviceGroupToAdd}/members`,
                                        { userId: editingUser._id, role: memberRole },
                                        { headers: { 'x-auth-token': token } }
                                    );
                                    setServiceGroupToAdd('');
                                    setServiceGroupRoleOverride('');
                                    fetchUsers();
                                    handleCloseEditModal();
                                } catch (err) {
                                    setError(err.response?.data?.msg || 'Failed to add to service group');
                                }
                            }}
                        >
                            Add
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                    <Button
                        color="warning"
                        onClick={() => {
                            setPasswordResetDialog({
                                open: true,
                                user: editingUser,
                                newPassword: '',
                                forceChange: true
                            });
                        }}
                        disabled={editFormLoading || editingUser?.role?.includes('admin')}
                    >
                        Reset Password
                    </Button>
                    <Button
                        size="small"
                        onClick={() => runSecurityAction(`/admin/users/${editingUser?._id}/unlock`, 'Sign-in lock lifted.')}
                        disabled={editFormLoading || !editingUser?._id}
                    >
                        Unlock sign-in
                    </Button>
                    <Button
                        size="small"
                        color="warning"
                        onClick={() => {
                            if (window.confirm(`Reset two-step sign-in for ${editingUser?.name || 'this user'}? They will be signed out and, if their role needs it, set it up again at their next sign-in.`)) {
                                runSecurityAction(`/admin/users/${editingUser?._id}/mfa/reset`, 'Two-step sign-in reset. They have been signed out.');
                            }
                        }}
                        disabled={editFormLoading || !editingUser?._id}
                    >
                        Reset two-step sign-in
                    </Button>
                    <Button
                        size="small"
                        onClick={() => runSecurityAction(`/admin/users/${editingUser?._id}/devices/forget`, 'Trusted devices forgotten. Each will ask for a code again.')}
                        disabled={editFormLoading || !editingUser?._id}
                    >
                        Forget trusted devices
                    </Button>
                    {securityActionMsg && <span style={{ fontSize: 13 }}>{securityActionMsg}</span>}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={handleCloseEditModal} disabled={editFormLoading}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveClick}
                            disabled={editFormLoading || editingUser?.role?.includes('admin')}
                        >
                            {editFormLoading ? <CircularProgress size={20} /> : 'Save Changes'}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* Activation Email Confirmation Dialog */}
            <Dialog open={activationEmailDialog.open} onClose={() => setActivationEmailDialog({ open: false, callback: null })}>
                <DialogTitle>Send Activation Email?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You are activating this user from Pending status. Would you like to send them an activation email notification?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => activationEmailDialog.callback?.(false)}>
                        No, Just Activate
                    </Button>
                    <Button variant="contained" onClick={() => activationEmailDialog.callback?.(true)}>
                        Yes, Send Email
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Password Reset Dialog */}
            <Dialog
                open={passwordResetDialog.open}
                onClose={() => setPasswordResetDialog({ open: false, user: null, newPassword: '', forceChange: true })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Reset Password for {passwordResetDialog.user?.name}</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <DialogContentText sx={{ mb: 2 }}>
                        Enter a new password for this user. The password must be at least 6 characters long.
                    </DialogContentText>

                    <TextField
                        fullWidth
                        margin="normal"
                        label="New Password"
                        type="password"
                        value={passwordResetDialog.newPassword}
                        onChange={(e) => setPasswordResetDialog(prev => ({ ...prev, newPassword: e.target.value }))}
                        autoFocus
                        helperText="Minimum 6 characters"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={passwordResetDialog.forceChange}
                                onChange={(e) => setPasswordResetDialog(prev => ({ ...prev, forceChange: e.target.checked }))}
                            />
                        }
                        label="Force user to change password on next login"
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPasswordResetDialog({ open: false, user: null, newPassword: '', forceChange: true })}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={async () => {
                            if (!passwordResetDialog.newPassword || passwordResetDialog.newPassword.length < 6) {
                                setError('Password must be at least 6 characters long');
                                return;
                            }

                            try {
                                setError('');
                                await resetUserPassword(
                                    passwordResetDialog.user._id,
                                    {
                                        newPassword: passwordResetDialog.newPassword,
                                        forcePasswordChange: passwordResetDialog.forceChange
                                    },
                                    localStorage.getItem('token')
                                );
                                alert('Password reset successfully!');
                                setPasswordResetDialog({ open: false, user: null, newPassword: '', forceChange: true });

                                // Update the edit form if it's the same user
                                if (editingUser?._id === passwordResetDialog.user._id) {
                                    setEditForm(f => ({ ...f, forcePasswordChange: passwordResetDialog.forceChange }));
                                }

                                fetchUsers(); // Refresh user list
                            } catch (err) {
                                setError(err.message || 'Failed to reset password');
                            }
                        }}
                        disabled={!passwordResetDialog.newPassword || passwordResetDialog.newPassword.length < 6}
                    >
                        Reset Password
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Therapist Settings Modal (existing) */}
            <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Therapist Settings for {selectedUser?.name}</DialogTitle>
                <DialogContent>
                    {modalError && <Alert severity="error" sx={{ mb: 2 }}>{modalError}</Alert>}
                    
                    <Typography variant="subtitle1" gutterBottom sx={{mt:1}}>Voice Configuration</Typography>
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="therapist-voice-select-label">Select ElevenLabs Voice</InputLabel>
                        <Select
                            labelId="therapist-voice-select-label"
                            value={elevenLabsVoiceIdField || ''}
                            onChange={(e) => setElevenLabsVoiceIdField(e.target.value)}
                            label="Select ElevenLabs Voice"
                        >
                            <MenuItem value="">
                                <em>None (uses platform default)</em>
                            </MenuItem>
                            {availableElevenLabsVoices.map((voice) => (
                                <MenuItem key={voice.voice_id} value={voice.voice_id}>
                                    {voice.name} ({voice.labels?.gender || 'N/A'}, {voice.labels?.accent || 'N/A'})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        margin="normal"
                        label="ElevenLabs Voice ID (Manual Entry)"
                        value={elevenLabsVoiceIdField}
                        onChange={(e) => setElevenLabsVoiceIdField(e.target.value)}
                        placeholder="Enter ElevenLabs Voice ID"
                        helperText="Selected from dropdown or enter manually. Leave blank to use platform default AI voice."
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        margin="normal"
                        label="Sample Text for Voice Preview"
                        value={sampleTextModal}
                        onChange={(e) => setSampleTextModal(e.target.value)}
                        multiline
                        rows={2}
                        sx={{ mb: 1 }}
                    />
                    {elevenLabsVoiceIdField && sampleTextModal && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>Preview:</Typography>
                            <TextToSpeech
                                text={sampleTextModal}
                                voiceId={elevenLabsVoiceIdField}
                                showVoiceSelector={false}
                                useStreaming={false} // REST for previews
                                size="small"
                            />
                        </Box>
                    )}
                    
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" gutterBottom>Specializations</Typography>
                    <FormControl fullWidth>
                        <InputLabel id="specialization-select-label">Specializations</InputLabel>
                        <Select
                            labelId="specialization-select-label"
                            multiple
                            value={selectedSpecializations}
                            onChange={handleSpecializationChange}
                            input={<OutlinedInput label="Specializations" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip key={value} label={value} size="small" />
                                    ))}
                                </Box>
                            )}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 224,
                                        width: 250,
                                    },
                                },
                            }}
                        >
                            {availableModalities.length === 0 && <MenuItem disabled>Loading modalities...</MenuItem>}
                            {availableModalities.map((modality) => (
                                <MenuItem key={modality} value={modality}>
                                    {modality}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleCloseModal} 
                        disabled={modalLoading}
                        className="user-management-3d-button"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSaveChanges} 
                        disabled={modalLoading}
                        className="user-management-3d-button"
                    >
                        {modalLoading ? <CircularProgress size={24} /> : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Status Change Confirmation Dialog */}
            <Dialog
                open={statusChangeDialog.open}
                onClose={() => setStatusChangeDialog({ open: false, user: null, newStatus: null })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Activate User Account</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You are about to activate the account for <strong>{statusChangeDialog.user?.name || statusChangeDialog.user?.email}</strong>.
                    </DialogContentText>
                    <DialogContentText sx={{ mt: 2 }}>
                        Would you like to send an activation email to notify the user that their account is now active?
                    </DialogContentText>
                    <Alert severity="info" sx={{ mt: 2 }}>
                        The email will be sent to: <strong>{statusChangeDialog.user?.email}</strong>
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setStatusChangeDialog({ open: false, user: null, newStatus: null })}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={() => {
                            handleStatusChange(statusChangeDialog.user._id, statusChangeDialog.newStatus, false);
                            setStatusChangeDialog({ open: false, user: null, newStatus: null });
                        }}
                        variant="outlined"
                    >
                        Activate Without Email
                    </Button>
                    <Button 
                        onClick={() => {
                            handleStatusChange(statusChangeDialog.user._id, statusChangeDialog.newStatus, true);
                            setStatusChangeDialog({ open: false, user: null, newStatus: null });
                        }}
                        variant="contained"
                        color="primary"
                    >
                        Activate & Send Email
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default UserManagement;
